import { Box, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { Table } from 'Common/MUITableV2'
import { GridSkeleton } from './GridSkeleton.view'
import { autoHeight } from 'Common/utils/utils'
import { useSchedulerData } from 'Features/SchedulerView/SchedulerView.data'
import { formatNumber } from './Utilisation.data'
import { GanttRow, toDetails, DetailsRow } from './Utilisation.derive'

type Props = {
  schedulerId: string
  iteration: string
}

const numCol = (
  field: keyof DetailsRow,
  headerName: string,
  digits = 2
): GridColDef<DetailsRow> => ({
  field: field as string,
  headerName,
  type: 'number',
  flex: 1,
  minWidth: 110,
  valueFormatter: (value: number) => formatNumber(value, digits),
})

const COLS: GridColDef<DetailsRow>[] = [
  {
    field: 'productCodeLineKitchen',
    headerName: 'ProductCodeLineKitchen',
    flex: 1,
    minWidth: 200,
  },
  { field: 'kitchen', headerName: 'Kitchen', flex: 1, minWidth: 90 },
  { field: 'line', headerName: 'Line', flex: 1, minWidth: 90 },
  { field: 'productCode', headerName: 'Product Code', flex: 1, minWidth: 110 },
  {
    field: 'product_description',
    headerName: 'Product Description',
    flex: 1,
    minWidth: 180,
  },
  { field: 'code_desc', headerName: 'Code Desc', flex: 1, minWidth: 180 },
  {
    field: 'inputProductCode',
    headerName: 'Input Code',
    flex: 1,
    minWidth: 110,
  },
  {
    field: 'inputProductName',
    headerName: 'Input Name',
    flex: 1,
    minWidth: 180,
  },
  {
    field: 'inputCode_desc',
    headerName: 'Input Code Desc',
    flex: 1,
    minWidth: 180,
  },
  numCol('max_kitchen_clean_end_time', 'Max Kitchen Clean End', 2),
  numCol('run_rate', 'Run Rate', 2),
  numCol('pr', 'PR', 2),
  numCol('sum_quantity', 'Quantity', 0),
  numCol('sum_base_material_qty', 'Base Material Qty', 1),
  numCol('sum_kitchen_run_time', 'Kitchen Run Time', 2),
  numCol('sum_kitchen_run_time_unique', 'Kitchen Run Time (Unique)', 2),
  numCol('sum_kitchen_clean_time', 'Kitchen Clean Time', 2),
  numCol('sum_line_run_time', 'Line Run Time', 2),
  numCol('sum_line_clean_time', 'Line Clean Time', 2),
]

export const DetailsView = ({ schedulerId, iteration }: Props) => {
  const { data, isLoading, error } = useSchedulerData(schedulerId, iteration)

  const rows = useMemo<DetailsRow[]>(() => {
    const gantt = (data?.data ?? []) as GanttRow[]
    return toDetails(gantt)
  }, [data])

  // Grid skeleton, not a spinner: same columns, same height, so the
  // real table replaces it without the layout moving.
  if (isLoading) return <GridSkeleton columns={COLS as GridColDef[]} />
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Could not load schedule data
          {error instanceof Error ? `: ${error.message}` : ''}.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className="table-container-mui">
      <Table
        ref={(el: HTMLDivElement | null) => el && autoHeight(el)}
        rows={rows}
        columns={COLS as GridColDef[]}
        density="compact"
        disableRowSelectionOnClick
        getRowClassName={(p) =>
          (p.indexRelativeToCurrentPage ?? 0) % 2 === 0
            ? 'row--even'
            : 'row--odd'
        }
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        pageSizeOptions={[25, 50, 100]}
      />
    </Box>
  )
}
